/* =====================================================================
   La conexión con la terminal por Web Bluetooth · contrato v2 2.5.0 §16
   =====================================================================
   Envuelve un `BluetoothDevice` (el que el navegador entregó al elegir
   TEGO-XXXX en su ventana) con el protocolo de `cfgBle.js`. Sin React:
   la pantalla (`ConfigurarPorBluetooth.jsx`) solo llama a estos métodos y
   pinta lo que devuelven. Se prueba con la terminal falsa de
   `pruebas/terminalBleFalsa.js`, en Node y en el navegador.

   LA VUELTA POR EL WI-FI (§16.5): al recibir WIFI la terminal apaga el
   Bluetooth para probar la red y lo vuelve a encender 30 s con el estado
   final. `reconectar()` reintenta `device.gatt.connect()` con EL MISMO
   dispositivo (sin otra ventana del navegador) durante ese tiempo.
   ===================================================================== */
import {
  ESTADO, Reensamblador, TIPO, UUID, aBytes, base64aBytes, derivarLlave, fragmentar,
  generarPar, msgCifrado, msgHola, msgSinDatos, parsearEstado, parsearRedes, datosWifi,
} from './cfgBle.js';

const esperar = (ms) => new Promise((r) => { setTimeout(r, ms); });

export class ErrorDeConexion extends Error {
  constructor(mensaje, clave = 'CONEXION') { super(mensaje); this.clave = clave; }
}

export class ConexionCfg {
  constructor(dispositivo, { subtle } = {}) {
    this.dispositivo = dispositivo;
    this.subtle = subtle;
    this.llave = null;
    this.info = null;
    this.estado = null;
    this.oyentes = new Set();
    this.cancelables = new Set();
    this.esperas = [];
    this.reensamblador = new Reensamblador();
    this.alDesconectar = null;
    this.desconectada = false;
    this._onDesconexion = () => {
      this.desconectada = true;
      if (this.alDesconectar) this.alDesconectar();
    };
    dispositivo.addEventListener?.('gattserverdisconnected', this._onDesconexion);
  }

  get nombre() { return this.dispositivo?.name || ''; }

  onEstado(fn) { this.oyentes.add(fn); return () => this.oyentes.delete(fn); }

  _nuevoEstado(e) {
    this.estado = e;
    for (const fn of this.oyentes) fn(e);
  }

  async conectar() {
    const servidor = await this.dispositivo.gatt.connect();
    this.desconectada = false;
    const servicio = await servidor.getPrimaryService(UUID.servicio);
    this.c = {
      info: await servicio.getCharacteristic(UUID.info),
      comando: await servicio.getCharacteristic(UUID.comando),
      estado: await servicio.getCharacteristic(UUID.estado),
      respuesta: await servicio.getCharacteristic(UUID.respuesta),
    };
    this.reensamblador = new Reensamblador();
    this.c.estado.addEventListener('characteristicvaluechanged', (ev) => {
      this._nuevoEstado(parsearEstado(aBytes(ev.target.value)));
    });
    this.c.respuesta.addEventListener('characteristicvaluechanged', (ev) => {
      const m = this.reensamblador.agregar(aBytes(ev.target.value));
      if (!m) return;
      const pendientes = this.esperas.filter((x) => x.tipo === m[0]);
      this.esperas = this.esperas.filter((x) => x.tipo !== m[0]);
      pendientes.forEach((x) => x.resolver(m));
    });
    await this.c.estado.startNotifications();
    await this.c.respuesta.startNotifications();
    return this.leerEstado();
  }

  async leerEstado() {
    const e = parsearEstado(aBytes(await this.c.estado.readValue()));
    this._nuevoEstado(e);
    return e;
  }

  async leerInfo() {
    const crudo = aBytes(await this.c.info.readValue());
    const info = JSON.parse(new TextDecoder().decode(crudo));
    if (info.protocolo !== 1 || !info.serie || !info.clave_publica) {
      throw new ErrorDeConexion('Esta terminal habla otra versión. Actualízala o usa el código corto.', 'VERSION');
    }
    this.info = info;
    return info;
  }

  async _escribir(mensaje) {
    for (const trozo of fragmentar(mensaje)) {
      const c = this.c.comando;
      if (c.writeValueWithResponse) await c.writeValueWithResponse(trozo);
      else await c.writeValue(trozo);
    }
  }

  /** HOLA: genera el par de la página y la llave; la terminal enseña 4 números. */
  async saludar() {
    if (!this.info) await this.leerInfo();
    const par = await generarPar(this.subtle);
    this.llave = await derivarLlave({
      privada: par.privada, publicaTerminal: base64aBytes(this.info.clave_publica),
      publicaPagina: par.publica, serie: this.info.serie, subtle: this.subtle,
    });
    await this._escribir(msgHola(par.publica));
  }

  /** Espera un estado que cumpla `pred` (o un ERROR). */
  esperarEstado(pred, ms = 15000) {
    let cancelar = null;
    const promesa = new Promise((resolver, rechazar) => {
      let quitar = null;
      const t = setTimeout(() => { quitar?.(); this.cancelables.delete(cancelar); rechazar(new ErrorDeConexion('La terminal no contestó a tiempo. Revisa que siga encendida y vuelve a empezar.', 'TIEMPO')); }, ms);
      quitar = this.onEstado((e) => {
        if (pred(e) || e.estado === ESTADO.ERROR) { clearTimeout(t); quitar(); this.cancelables.delete(cancelar); resolver(e); }
      });
      cancelar = () => { clearTimeout(t); quitar(); rechazar(new ErrorDeConexion('Se cerró la conexión con la terminal.', 'CERRADA')); };
      this.cancelables.add(cancelar);
    });
    // Nadie queda con un rechazo sin atender si la pantalla abandona la
    // espera (auditoría BLE P1-1); quien la espera con `await` lo recibe igual.
    promesa.catch(() => {});
    return promesa;
  }

  async confirmar(numeros) {
    const espera = this.esperarEstado((e) => e.estado === ESTADO.LISTA);
    await this._escribir(await msgCifrado(this.llave, TIPO.CONFIRMAR, String(numeros), this.subtle));
    return espera;
  }

  async pedirRedes(ms = 10000) {
    const llega = new Promise((resolver, rechazar) => {
      const t = setTimeout(() => rechazar(new ErrorDeConexion('La terminal no mandó su lista de redes. Vuelve a intentarlo.', 'TIEMPO')), ms);
      this.esperas.push({ tipo: TIPO.RESP_REDES, resolver: (m) => { clearTimeout(t); resolver(m); } });
    });
    await this._escribir(msgSinDatos(TIPO.REDES));
    return parsearRedes(await llega);
  }

  async enviarCredencial(vinculada) {
    const json = typeof vinculada === 'string' ? vinculada : JSON.stringify(vinculada);
    await this._escribir(await msgCifrado(this.llave, TIPO.CREDENCIAL, json, this.subtle));
  }

  /** WIFI. La contraseña solo vive en este mensaje cifrado. */
  async enviarWifi(red, clave, direccion = '') {
    await this._escribir(await msgCifrado(this.llave, TIPO.WIFI, datosWifi(red, clave, direccion), this.subtle));
  }

  async identificar() { await this._escribir(msgSinDatos(TIPO.IDENTIFICAR)); }

  async terminar() {
    try { await this._escribir(msgSinDatos(TIPO.TERMINAR)); } catch { /* ya se apagó: da igual */ }
  }

  /** Tras el WIFI: reintenta con el MISMO dispositivo durante `totalMs`. */
  async reconectar({ totalMs = 35000, cadaMs = 1500 } = {}) {
    const limite = Date.now() + totalMs;
    let ultimo = null;
    while (Date.now() < limite) {
      try {
        return await this.conectar();
      } catch (e) {
        ultimo = e;
        await esperar(cadaMs);
      }
    }
    throw new ErrorDeConexion('La terminal no volvió a aparecer. Revisa su pantalla: si dice «¡Lista!», ya quedó; si no, vuelve a empezar.', ultimo?.clave || 'TIEMPO');
  }

  /** Espera a que la terminal apague el Bluetooth (tras WIFI). */
  esperarDesconexion(ms = 12000) {
    if (this.desconectada) return Promise.resolve(true);
    return new Promise((resolver) => {
      const t = setTimeout(() => { this.alDesconectar = previo; resolver(false); }, ms);
      const previo = this.alDesconectar;
      this.alDesconectar = () => { clearTimeout(t); this.alDesconectar = previo; previo?.(); resolver(true); };
    });
  }

  /** §16.5 completo: WIFI ya enviado → la terminal se apaga → vuelve con
   *  el estado final. Devuelve ese estado (VINCULADA, CONECTADA o ERROR). */
  async vueltaPorElWifi({ totalMs = 45000, cadaMs = 1500 } = {}) {
    const inicio = Date.now();
    const seFue = await this.esperarDesconexion(12000);
    if (!seFue) {
      // No se apagó: un ERROR inmediato (red que no ve, contraseña corta).
      if (this.estado?.estado === ESTADO.ERROR) return this.estado;
      return this.leerEstado();
    }
    let e = await this.reconectar({ totalMs: totalMs - (Date.now() - inicio), cadaMs });
    if (e.estado === ESTADO.CONECTANDO) {
      e = await this.esperarEstado((x) => x.estado !== ESTADO.CONECTANDO,
        Math.max(1000, totalMs - (Date.now() - inicio)));
    }
    return e;
  }

  desconectar() {
    for (const c of [...this.cancelables]) c();
    this.cancelables.clear();
    try { this.dispositivo.gatt?.disconnect?.(); } catch { /* nada */ }
  }
}

/** Abre la ventana del navegador para elegir una terminal TEGO-XXXX. */
export async function elegirTerminal(nav = globalThis.navigator) {
  return nav.bluetooth.requestDevice({
    filters: [{ services: [UUID.servicio] }, { namePrefix: 'TEGO-' }],
    optionalServices: [UUID.servicio],
  });
}
